import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  Paper,
  Button,
  TextField,
  MenuItem,
  Select,
  IconButton,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Chip,
  Alert,
  Tooltip,
} from "@mui/material";
import axios from "axios";
import Layout from "../../layout/Layout";
import moment from "moment";
import { config, baseURL } from "../../config";
import * as XLSX from "xlsx/xlsx.mjs";

// Icons
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EditIcon from "@mui/icons-material/Edit";
import LockIcon from "@mui/icons-material/Lock";
import PaymentIcon from "@mui/icons-material/Payment";
import RefreshIcon from "@mui/icons-material/Refresh";

function PayrollProcess() {
  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState("");
  const [selectedMonth, setSelectedMonth] = useState(moment().format("YYYY-MM"));
  const [startDate, setStartDate] = useState(moment().subtract(1, "months").date(26).format("YYYY-MM-DD"));
  const [endDate, setEndDate] = useState(moment().date(25).format("YYYY-MM-DD"));

  // Batch states
  const [currentBatch, setCurrentBatch] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [subFilter, setSubFilter] = useState("all");
  const [nameFilter, setNameFilter] = useState("");
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [selectedExportColumns, setSelectedExportColumns] = useState({
    employee_name: true,
    employee_no: true,
    base_salary: true,
    month_days: true,
    present_days: true,
    absent_days: true,
    compensatory_days: true,
    casual_leaves: true,
    granted_working_days: true,
    one_day_salary: true,
    one_hr_salary: true,
    gross_pay: true,
    other_allowance: true,
    epf_fund: true,
    rd_esi: true,
    salary_advance: true,
    personal_advance: true,
    professional_tax: true,
    tds: true,
    manual_adjustments: true,
    total_deductions: true,
    net_pay: true,
    status: true,
  });

  const exportColumnLabels = {
    employee_name: "Employee Name",
    employee_no: "Employee No",
    base_salary: "Actual Salary",
    month_days: "Month Days",
    present_days: "Present Days",
    absent_days: "Absent Days",
    compensatory_days: "Compensatory Days",
    casual_leaves: "Casual Leave",
    granted_working_days: "Granted Working Days",
    one_day_salary: "One Day Salary",
    one_hr_salary: "1 Hr Salary",
    gross_pay: "Gross Salary",
    other_allowance: "Other Allowance",
    epf_fund: "PF",
    rd_esi: "ESI",
    salary_advance: "Salary Advance",
    personal_advance: "Personal Advance",
    professional_tax: "PT",
    tds: "TDS",
    manual_adjustments: "Other Deductions/Fine",
    total_deductions: "Total Deduction",
    net_pay: "Total Salary",
    status: "Status",
  };

  // Modal / Inputs state
  const [editEmployee, setEditEmployee] = useState(null);
  const [inputModalOpen, setInputModalOpen] = useState(false);
  const [inputForm, setInputForm] = useState({
    base_salary: 0,
    salary_advance: 0,
    personal_advance: 0,
    professional_tax: 0,
    tds: 0,
    variable_allowance: 0,
    other_allowance: 0,
    special_incentive: 0,
    manual_adjustments: 0,
    pf_applicable: true,
    esi_applicable: true,
    status: "Draft",
  });

  const userData = JSON.parse(localStorage.getItem("user") || "{}");

  useEffect(() => {
    // Load companies
    axios({
      ...config,
      method: "POST",
      url: `${baseURL}/manage-companies`,
      data: JSON.stringify({ intType: 5 }),
    })
      .then((res) => {
        setCompanies(res.data.result || []);
        if (res.data.result?.length > 0) {
          setSelectedCompany(res.data.result[0].id);
        }
      })
      .catch((err) => console.error("Error loading companies", err));
  }, []);

  const fetchPayrollBatchData = () => {
    if (!selectedCompany) return;
    setLoading(true);

    axios({
      method: "GET",
      url: `${baseURL}/payroll/list/${selectedCompany}`,
    })
      .then((res) => {
        const batchList = res.data.result || [];
        const active = batchList.find((b) => b.period_month_year === selectedMonth);
        
        if (active) {
          setCurrentBatch(active);
          setStartDate(moment(active.cycle_start_date).format("YYYY-MM-DD"));
          setEndDate(moment(active.cycle_end_date).format("YYYY-MM-DD"));
          
          // Load batch employees
          axios.get(`${baseURL}/payroll/batch/${active.id}/employees`).then((empRes) => {
            setEmployees(empRes.data.result || []);
          });
        } else {
          setCurrentBatch(null);
          setEmployees([]);
        }
      })
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchPayrollBatchData();
  }, [selectedCompany, selectedMonth]);

  useEffect(() => {
    if (selectedMonth) {
      setStartDate(moment(selectedMonth).subtract(1, "months").date(26).format("YYYY-MM-DD"));
      setEndDate(moment(selectedMonth).date(25).format("YYYY-MM-DD"));
    }
  }, [selectedMonth]);

  const handleCreateBatch = () => {
    setLoading(true);
    axios({
      method: "POST",
      url: `${baseURL}/payroll/batch/create`,
      data: {
        company_id: selectedCompany,
        month_year: selectedMonth,
        start_date: startDate,
        end_date: endDate,
        emp_id: userData.emp_id || 1,
      },
    })
      .then(() => {
        fetchPayrollBatchData();
      })
      .catch((err) => alert(err.response?.data?.message || "Failed to create batch"))
      .finally(() => setLoading(false));
  };

  const handleEditInputs = (row) => {
    setEditEmployee(row);
    setInputForm({
      base_salary: row.base_salary || 0,
      salary_advance: row.salary_advance || 0,
      personal_advance: row.personal_advance || 0,
      professional_tax: row.professional_tax || 0,
      tds: row.tds || 0,
      variable_allowance: row.variable_allowance || 0,
      other_allowance: row.other_allowance || 0,
      special_incentive: row.special_incentive || 0,
      manual_adjustments: row.manual_adjustments || 0,
      pf_applicable: row.pf_applicable !== 0,
      esi_applicable: row.esi_applicable !== 0,
      status: row.status || "Draft",
    });
    setInputModalOpen(true);
  };

  const handleSaveInputs = () => {
    axios({
      method: "PUT",
      url: `${baseURL}/payroll/employee/${editEmployee.payslip_id}/input`,
      data: inputForm,
    }).then(() => {
      setInputModalOpen(false);
      fetchPayrollBatchData();
    });
  };

  const handleToggleEmployee = (row, isChecked) => {
    const newStatus = isChecked ? "Draft" : "Hold";
    axios({
      method: "PUT",
      url: `${baseURL}/payroll/employee/${row.payslip_id}/input`,
      data: {
        ...row,
        status: newStatus
      }
    }).then(() => {
      fetchPayrollBatchData();
    });
  };

  const handleToggleSelectAll = (isChecked) => {
    const newStatus = isChecked ? "Draft" : "Hold";
    const employeeIds = employees.map(emp => emp.employee_id);
    
    axios({
      method: "POST",
      url: `${baseURL}/payroll/batch/${currentBatch.id}/action/bulk-status`,
      data: {
        status: newStatus,
        employeeIds
      }
    }).then(() => {
      fetchPayrollBatchData();
    });
  };

  const handleResetBatch = () => {
    if (window.confirm("Are you sure you want to reset this payroll batch? All manual overrides will be lost.")) {
      setLoading(true);
      axios({
        method: "DELETE",
        url: `${baseURL}/payroll/batch/${currentBatch.id}`,
      })
        .then(() => {
          fetchPayrollBatchData();
        })
        .catch((err) => alert(err.response?.data?.message || "Failed to reset batch"))
        .finally(() => setLoading(false));
    }
  };

  const handleProcessAction = (action) => {
    if (action === "lock") {
      const processedCount = employees.filter(emp => emp.status === "Processed").length;
      const totalCount = employees.length;
      if (!window.confirm(`Are you sure you want to approve payroll? Payslips will only be generated for the ${processedCount} processed employees. The remaining ${totalCount - processedCount} employees on hold/unprocessed will be skipped until verified.`)) {
        return;
      }
    }
    setLoading(true);
    axios({
      method: "POST",
      url: `${baseURL}/payroll/batch/${currentBatch.id}/action/${action}`,
    })
      .then((res) => {
        alert(res.data.message);
        fetchPayrollBatchData();
      })
      .catch((err) => alert(err.response?.data?.message || "Action failed"))
      .finally(() => setLoading(false));
  };

  const handleProcessSingleEmployee = (payslipId) => {
    setLoading(true);
    axios({
      method: "POST",
      url: `${baseURL}/payroll/employee/${payslipId}/process`,
    })
      .then((res) => {
        alert(res.data.message);
        fetchPayrollBatchData();
      })
      .catch((err) => alert(err.response?.data?.message || "Calculation failed"))
      .finally(() => setLoading(false));
  };

  const downloadExcel = () => {
    const filteredEmployees = employees
      .filter((emp) => {
        if (subFilter === "active") return emp.status !== "Hold" && emp.status !== "Stopped";
        if (subFilter === "hold") return emp.status === "Hold" || emp.status === "Stopped";
        return true;
      })
      .filter((emp) => {
        if (!nameFilter) return true;
        return emp.employee_name.toLowerCase().includes(nameFilter.toLowerCase()) ||
               (emp.employee_no && emp.employee_no.toLowerCase().includes(nameFilter.toLowerCase()));
      });

    const newData = filteredEmployees.map((row) => {
      const exportRow = {};
      const oneDaySalary = row.month_days > 0 ? parseFloat(row.base_salary || 0) / row.month_days : 0;
      const oneHrSalary = oneDaySalary / 8;
      const otherAllowance = 
        parseFloat(row.house_allowance || 0) + 
        parseFloat(row.medical_allowance || 0) + 
        parseFloat(row.vehicle_allowance || 0) + 
        parseFloat(row.other_allowance || 0) + 
        parseFloat(row.variable_allowance || 0) + 
        parseFloat(row.special_incentive || 0);

      Object.keys(selectedExportColumns).forEach((key) => {
        if (selectedExportColumns[key]) {
          const label = exportColumnLabels[key];
          if (key === "other_allowance") {
            exportRow[label] = otherAllowance;
          } else if (key === "one_day_salary") {
            exportRow[label] = oneDaySalary.toFixed(2);
          } else if (key === "one_hr_salary") {
            exportRow[label] = oneHrSalary.toFixed(2);
          } else {
            exportRow[label] = row[key] !== undefined ? row[key] : "";
          }
        }
      });
      return exportRow;
    });

    const workSheet = XLSX.utils.json_to_sheet(newData);
    const workBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workBook, workSheet, "Worksheets");
    XLSX.writeFile(
      workBook,
      `Salary-Statement-${selectedMonth}.xlsx`
    );
    setExportDialogOpen(false);
  };

  const getStats = () => {
    let activeCount = 0;
    let totalDeductions = 0;
    let grossPay = 0;
    let netPayout = 0;

    employees.forEach((emp) => {
      if (emp.status !== "Hold" && emp.status !== "Stopped") {
        activeCount++;
        totalDeductions += parseFloat(emp.total_deductions || 0);
        grossPay += parseFloat(emp.gross_pay || 0);
        netPayout += parseFloat(emp.net_pay || 0);
      }
    });

    return {
      activeCount,
      totalDeductions,
      grossPay,
      netPayout,
    };
  };

  const stats = getStats();

  const getStatusChipColor = (status) => {
    switch (status) {
      case "Draft":
        return "default";
      case "Processed":
        return "primary";
      case "Locked":
        return "warning";
      case "Paid":
        return "success";
      default:
        return "default";
    }
  };

  return (
    <Layout>
      <Box sx={{ padding: "20px 25px", pt: "95px", background: "#f8fafc", minHeight: "100vh", fontFamily: "'Inter', sans-serif" }}>
        
        {/* HEADER FILTERS CARD */}
        <Card sx={{ p: 3, mb: 4, borderRadius: "16px", boxShadow: "0 1px 3px rgba(0,0,0,0.02)", border: "1px solid #e2e8f0" }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} sx={{ mb: 1 }}>
              <Typography variant="h5" fontWeight="800" color="#0f172a">
                Payroll Management
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Select
                fullWidth
                size="small"
                value={selectedCompany}
                onChange={(e) => setSelectedCompany(e.target.value)}
                disabled={!!currentBatch}
                sx={{ borderRadius: "8px" }}
              >
                {companies.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.company_name}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid item xs={12} sm={6} md={2.25}>
              <TextField
                fullWidth
                type="month"
                size="small"
                label="Period Month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                disabled={!!currentBatch}
                InputLabelProps={{ shrink: true }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.25}>
              <TextField
                fullWidth
                type="date"
                size="small"
                label="From Date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                disabled={!!currentBatch}
                InputLabelProps={{ shrink: true }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.25}>
              <TextField
                fullWidth
                type="date"
                size="small"
                label="To Date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                disabled={!!currentBatch}
                InputLabelProps={{ shrink: true }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
              />
            </Grid>
             <Grid item xs={12} sm={6} md={2.25}>
              {!currentBatch && (
                <Button
                  fullWidth
                  variant="contained"
                  disabled={loading}
                  onClick={handleCreateBatch}
                  sx={{ borderRadius: "8px", textTransform: "none", fontWeight: "600", background: "linear-gradient(135deg, #2563EB 0%, #4F46E5 100%)", py: 1 }}
                >
                  {loading ? "Starting..." : "Start Payroll"}
                </Button>
              )}
            </Grid>
          </Grid>
        </Card>

        {currentBatch ? (
          <>
            {/* KPI STATS ROW */}
            <Grid container spacing={3} sx={{ mb: 4 }}>              
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ p: 2.5, borderRadius: "16px", border: "1px solid #e2e8f0", background: "#ffffff", boxShadow: "0 1px 3px rgba(0,0,0,0.02)" }}>
                  <Tooltip title="Base earned salary plus all allowances before deductions">
                    <Typography variant="caption" color="textSecondary" fontWeight="700" sx={{ cursor: 'pointer', borderBottom: '1px dashed #cbd5e1' }}>
                      TOTAL GROSS SALARY
                    </Typography>
                  </Tooltip>
                  <Typography variant="h5" fontWeight="800" color="#0f172a" sx={{ mt: 1 }}>
                    ₹ {stats.grossPay.toLocaleString("en-IN")}
                  </Typography>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ p: 2.5, borderRadius: "16px", border: "1px solid #e2e8f0", background: "#ffffff", boxShadow: "0 1px 3px rgba(0,0,0,0.02)" }}>
                  <Tooltip title="Actual employee take-home salary after deductions (PF, ESI, TDS, PT, advances)">
                    <Typography variant="caption" color="textSecondary" fontWeight="700" sx={{ cursor: 'pointer', borderBottom: '1px dashed #cbd5e1' }}>
                      NET PAYABLE SALARY
                    </Typography>
                  </Tooltip>
                  <Typography variant="h5" fontWeight="800" color="#1e3a8a" sx={{ mt: 1 }}>
                    ₹ {stats.netPayout.toLocaleString("en-IN")}
                  </Typography>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ p: 2.5, borderRadius: "16px", border: "1px solid #e2e8f0", background: "#ffffff", boxShadow: "0 1px 3px rgba(0,0,0,0.02)" }}>
                  <Tooltip title="Sum of PF, ESI, TDS, Professional Tax, and Advances to be deducted">
                    <Typography variant="caption" color="textSecondary" fontWeight="700" sx={{ cursor: 'pointer', borderBottom: '1px dashed #cbd5e1' }}>
                      TOTAL DEDUCTIONS
                    </Typography>
                  </Tooltip>
                  <Typography variant="h5" fontWeight="800" color="#ef4444" sx={{ mt: 1 }}>
                    ₹ {stats.totalDeductions.toLocaleString("en-IN")}
                  </Typography>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ p: 2.5, borderRadius: "16px", border: "1px solid #e2e8f0", background: "#ffffff", boxShadow: "0 1px 3px rgba(0,0,0,0.02)" }}>
                  <Typography variant="caption" color="textSecondary" fontWeight="700">ACTIVE PAYROLL STATUS</Typography>
                  <Box display="flex" alignItems="center" gap={1.5} sx={{ mt: 1 }}>
                    <Chip label={currentBatch.status} color={getStatusChipColor(currentBatch.status)} fontWeight="bold" />
                    <Typography variant="body2" color="textSecondary" fontWeight="600">
                      ({stats.activeCount} Workers)
                    </Typography>
                  </Box>
                </Card>
              </Grid>
            </Grid>

            {/* ACTION CONTROLS PANEL */}
            <Card sx={{ p: 3, mb: 4, borderRadius: "16px", border: "1px solid #e2e8f0" }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
                <Box>
                  <Typography variant="subtitle1" fontWeight="700" color="#1e293b">
                    Cycle Range: {moment(currentBatch.cycle_start_date).format("DD MMM YYYY")} - {moment(currentBatch.cycle_end_date).format("DD MMM YYYY")}
                  </Typography>
                  <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: "block" }}>
                    Verify attendance and add manual inputs/overrides in the list below before locking.
                  </Typography>
                </Box>
                
                <Box display="flex" gap={2}>
                  {currentBatch.status === "Draft" && (
                    <Button
                      variant="contained"
                      disabled={loading}
                      onClick={() => handleProcessAction("process")}
                      startIcon={<CheckCircleIcon />}
                      sx={{ textTransform: "none", borderRadius: "8px", fontWeight: "600" }}
                    >
                      {loading ? "Calculating..." : "Calculate Salary"}
                    </Button>
                  )}

                  {currentBatch.status === "Processed" && (
                    <Button
                      variant="contained"
                      color="warning"
                      disabled={loading}
                      onClick={() => handleProcessAction("lock")}
                      startIcon={<LockIcon />}
                      sx={{ textTransform: "none", borderRadius: "8px", fontWeight: "600" }}
                    >
                      {loading ? "Approving..." : "Approve Payroll"}
                    </Button>
                  )}

                  {currentBatch.status === "Locked" && (
                    <Button
                      variant="contained"
                      color="success"
                      disabled={loading}
                      onClick={() => handleProcessAction("pay")}
                      startIcon={<PaymentIcon />}
                      sx={{ textTransform: "none", borderRadius: "8px", fontWeight: "600" }}
                    >
                      {loading ? "Confirming..." : "Confirm Payment"}
                    </Button>
                  )}

                  <Button
                    variant="outlined"
                    color="error"
                    disabled={loading}
                    onClick={handleResetBatch}
                    startIcon={<RefreshIcon />}
                    sx={{ textTransform: "none", borderRadius: "8px", fontWeight: "600" }}
                  >
                    Reset Payroll
                  </Button>
                </Box>
              </Box>

              {currentBatch.status === "Draft" && (
                <Alert severity="info" sx={{ mt: 2, borderRadius: "8px" }}>
                  💡 HR Step: Click <strong>Calculate Salary</strong> to compute salary splits, EPF, and ESI based on the final presence days and input adjustments.
                </Alert>
              )}
              {currentBatch.status === "Processed" && (
                <Alert severity="warning" sx={{ mt: 2, borderRadius: "8px" }}>
                   Owner Review: Verify final payable salaries below. Click <strong>Approve Payroll</strong> to lock it for payslip generation.
                </Alert>
              )}
              {currentBatch.status === "Locked" && (
                <Alert severity="success" sx={{ mt: 2, borderRadius: "8px" }}>
                  🔒 Frozen: Payroll has been approved. You can now generate PDF payslips for this month under the Payslip Reports module.
                </Alert>
              )}
            </Card>

            {/* EMPLOYEES LIST TABLE WORKSPACE */}
            <Card sx={{ p: 3, borderRadius: "16px", border: "1px solid #e2e8f0" }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} sx={{ mb: 3 }}>
                <Typography variant="h6" fontWeight="800" color="#0f172a">
                  Employee Payroll Worksheets
                </Typography>
                
                <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
                  <TextField
                    placeholder="Search by name"
                    variant="outlined"
                    size="small"
                    value={nameFilter}
                    onChange={(e) => setNameFilter(e.target.value)}
                    sx={{ width: 200, "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={() => setExportDialogOpen(true)}
                    sx={{ textTransform: "none", borderRadius: "8px", fontWeight: "600", height: "40px" }}
                  >
                    Export to Excel
                  </Button>
                  
                  {/* SUB FILTER ACTION TABS */}
                  <Box display="flex" gap={1} sx={{ ml: 1 }}>
                    <Button
                      variant={subFilter === "all" ? "contained" : "outlined"}
                      size="small"
                      onClick={() => setSubFilter("all")}
                      sx={{ textTransform: "none", borderRadius: "8px", fontWeight: "600" }}
                    >
                      All ({employees.length})
                    </Button>
                    <Button
                      variant={subFilter === "active" ? "contained" : "outlined"}
                      size="small"
                      color="success"
                      onClick={() => setSubFilter("active")}
                      sx={{ textTransform: "none", borderRadius: "8px", fontWeight: "600" }}
                    >
                      Active ({employees.filter(e => e.status !== "Hold" && e.status !== "Stopped").length})
                    </Button>
                    <Button
                      variant={subFilter === "hold" ? "contained" : "outlined"}
                      size="small"
                      color="warning"
                      onClick={() => setSubFilter("hold")}
                      sx={{ textTransform: "none", borderRadius: "8px", fontWeight: "600" }}
                    >
                      On Hold ({employees.filter(e => e.status === "Hold" || e.status === "Stopped").length})
                    </Button>
                  </Box>
                </Box>
              </Box>

              <TableContainer component={Paper} sx={{ borderRadius: "12px", border: "1px solid #e2e8f0", overflowX: "auto", maxHeight: "600px" }}>
                <Table sx={{ minWidth: 1200 }} stickyHeader>
                  <TableHead sx={{ background: "#f8fafc" }}>
                    <TableRow>
                      <TableCell padding="checkbox" sx={{ position: "sticky", left: 0, background: "#f8fafc", zIndex: 3, fontWeight: "700" }}>
                        <Checkbox
                          checked={
                            employees.length > 0 &&
                            employees.filter(emp => subFilter === "all" ? true : (subFilter === "active" ? (emp.status !== "Hold" && emp.status !== "Stopped") : (emp.status === "Hold" || emp.status === "Stopped"))).every(emp => emp.status !== "Hold" && emp.status !== "Stopped")
                          }
                          indeterminate={
                            employees.filter(emp => subFilter === "all" ? true : (subFilter === "active" ? (emp.status !== "Hold" && emp.status !== "Stopped") : (emp.status === "Hold" || emp.status === "Stopped"))).some(emp => emp.status !== "Hold" && emp.status !== "Stopped") &&
                            !employees.filter(emp => subFilter === "all" ? true : (subFilter === "active" ? (emp.status !== "Hold" && emp.status !== "Stopped") : (emp.status === "Hold" || emp.status === "Stopped"))).every(emp => emp.status !== "Hold" && emp.status !== "Stopped")
                          }
                          onChange={(e) => handleToggleSelectAll(e.target.checked)}
                          disabled={currentBatch.status !== "Draft"}
                        />
                      </TableCell>
                      <TableCell sx={{ position: "sticky", left: 48, background: "#f8fafc", zIndex: 3, fontWeight: "700" }}>S.No</TableCell>
                      <TableCell sx={{ position: "sticky", left: 100, background: "#f8fafc", zIndex: 3, fontWeight: "700" }}>Name</TableCell>
                      <TableCell sx={{ fontWeight: "700" }}>Actual Salary</TableCell>
                      <TableCell sx={{ fontWeight: "700" }}>Month Days</TableCell>
                      <TableCell sx={{ fontWeight: "700" }}>Present Days</TableCell>
                      <TableCell sx={{ fontWeight: "700" }}>Absent Days</TableCell>
                      <TableCell sx={{ fontWeight: "700" }}>Compensatory Days</TableCell>
                      <TableCell sx={{ fontWeight: "700" }}>Casual Leave</TableCell>
                      <TableCell sx={{ fontWeight: "700" }}>Granted Working Days</TableCell>
                      <TableCell sx={{ fontWeight: "700" }}>One Day Salary</TableCell>
                      <TableCell sx={{ fontWeight: "700" }}>1 Hr Salary</TableCell>
                      <TableCell sx={{ fontWeight: "700" }}>Gross Salary</TableCell>
                      <TableCell sx={{ fontWeight: "700" }}>Other Allowance</TableCell>
                      <TableCell sx={{ fontWeight: "700" }}>Net Payable Salary</TableCell>
                      <TableCell sx={{ fontWeight: "700" }}>PF</TableCell>
                      <TableCell sx={{ fontWeight: "700" }}>ESI</TableCell>
                      <TableCell sx={{ fontWeight: "700" }}>Salary Advance</TableCell>
                      <TableCell sx={{ fontWeight: "700" }}>Personal Advance</TableCell>
                      <TableCell sx={{ fontWeight: "700" }}>PT</TableCell>
                      <TableCell sx={{ fontWeight: "700" }}>TDS</TableCell>
                      <TableCell sx={{ fontWeight: "700" }}>Other Deductions/Fine</TableCell>
                      <TableCell sx={{ fontWeight: "700" }}>Total Deduction</TableCell>
                      <TableCell sx={{ fontWeight: "700" }}>Total Salary</TableCell>
                      <TableCell sx={{ fontWeight: "700" }}>Status</TableCell>
                      <TableCell align="center" sx={{ fontWeight: "700" }}>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {employees
                      .filter((emp) => {
                        if (subFilter === "active") return emp.status !== "Hold" && emp.status !== "Stopped";
                        if (subFilter === "hold") return emp.status === "Hold" || emp.status === "Stopped";
                        return true;
                      })
                      .filter((emp) => {
                        if (!nameFilter) return true;
                        return emp.employee_name.toLowerCase().includes(nameFilter.toLowerCase()) ||
                               (emp.employee_no && emp.employee_no.toLowerCase().includes(nameFilter.toLowerCase()));
                      })
                      .map((row, index) => {
                        const oneDaySalary = row.month_days > 0 ? parseFloat(row.base_salary || 0) / row.month_days : 0;
                        const oneHrSalary = oneDaySalary / 8;
                        const otherAllowance = 
                          parseFloat(row.house_allowance || 0) + 
                          parseFloat(row.medical_allowance || 0) + 
                          parseFloat(row.vehicle_allowance || 0) + 
                          parseFloat(row.other_allowance || 0) + 
                          parseFloat(row.variable_allowance || 0) + 
                          parseFloat(row.special_incentive || 0);

                        const formatDays = (val) => {
                          if (val === undefined || val === null || val === "") return "-";
                          const parsed = parseFloat(val);
                          return isNaN(parsed) ? "-" : Number(parsed);
                        };

                        return (
                          <TableRow key={row.payslip_id}>
                            <TableCell padding="checkbox" sx={{ position: "sticky", left: 0, background: "#fff", zIndex: 1 }}>
                              <Checkbox
                                checked={row.status !== "Hold" && row.status !== "Stopped"}
                                onChange={(e) => handleToggleEmployee(row, e.target.checked)}
                                disabled={currentBatch.status === "Paid"}
                              />
                            </TableCell>
                            <TableCell sx={{ position: "sticky", left: 48, background: "#fff", zIndex: 1 }}>{index + 1}</TableCell>
                            <TableCell sx={{ position: "sticky", left: 100, background: "#fff", zIndex: 1 }}>{row.employee_name}</TableCell>
                            <TableCell>₹{parseFloat(row.base_salary || 0).toLocaleString("en-IN")}</TableCell>
                            <TableCell>{formatDays(row.month_days)}</TableCell>
                            <TableCell>{formatDays(row.present_days)}</TableCell>
                            <TableCell>{formatDays(row.absent_days)}</TableCell>
                            <TableCell>{formatDays(row.compensatory_days)}</TableCell>
                            <TableCell>{formatDays(row.casual_leaves)}</TableCell>
                            <TableCell>{formatDays(row.granted_working_days)}</TableCell>
                            <TableCell>₹{oneDaySalary.toFixed(2)}</TableCell>
                            <TableCell>₹{oneHrSalary.toFixed(2)}</TableCell>
                            <TableCell>₹{parseFloat(row.gross_pay || 0).toLocaleString("en-IN")}</TableCell>
                            <TableCell>₹{otherAllowance.toLocaleString("en-IN")}</TableCell>
                            <TableCell sx={{ fontWeight: "600" }}>₹{parseFloat(row.gross_pay || 0).toLocaleString("en-IN")}</TableCell>
                            <TableCell>₹{parseFloat(row.epf_fund || 0).toLocaleString("en-IN")}</TableCell>
                            <TableCell>₹{parseFloat(row.rd_esi || 0).toLocaleString("en-IN")}</TableCell>
                            <TableCell>₹{parseFloat(row.salary_advance || 0).toLocaleString("en-IN")}</TableCell>
                            <TableCell>₹{parseFloat(row.personal_advance || 0).toLocaleString("en-IN")}</TableCell>
                            <TableCell>₹{parseFloat(row.professional_tax || 0).toLocaleString("en-IN")}</TableCell>
                            <TableCell>₹{parseFloat(row.tds || 0).toLocaleString("en-IN")}</TableCell>
                            <TableCell>₹{parseFloat(row.manual_adjustments || 0).toLocaleString("en-IN")}</TableCell>
                            <TableCell>₹{parseFloat(row.total_deductions || 0).toLocaleString("en-IN")}</TableCell>
                            <TableCell sx={{ fontWeight: "bold", color: "#1e3a8a" }}>₹{parseFloat(row.net_pay || 0).toLocaleString("en-IN")}</TableCell>
                            <TableCell>{row.status}</TableCell>
                            <TableCell align="center">
                              <Box display="flex" gap={0.5} justifyContent="center">
                                <IconButton onClick={() => handleEditInputs(row)} disabled={currentBatch.status === "Paid"}>
                                  <EditIcon />
                                </IconButton>
                                {(row.status === "Draft" || row.status === "Hold") && currentBatch.status !== "Paid" && (
                                  <Tooltip title="Calculate / Process Employee">
                                    <IconButton color="primary" onClick={() => handleProcessSingleEmployee(row.payslip_id)}>
                                      <CheckCircleIcon />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          </>
        ) : (
          <Card sx={{ p: 4, textAlign: "center", borderRadius: "16px" }}>
            <Typography variant="h6" color="textSecondary">
              No active payroll process started for this company and month.
            </Typography>
            <Button 
              variant="outlined" 
              disabled={loading}
              onClick={handleCreateBatch} 
              sx={{ mt: 2, textTransform: "none", borderRadius: "8px" }}
            >
              {loading ? "Starting..." : "Start Payroll Now"}
            </Button>
          </Card>
        )}

        {/* INPUTS EDIT DIALOG */}
        <Dialog open={inputModalOpen} onClose={() => setInputModalOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Edit Payroll Inputs</DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Base Salary"
                  type="number"
                  value={inputForm.base_salary}
                  onChange={(e) => setInputForm({ ...inputForm, base_salary: parseFloat(e.target.value) || 0 })}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Salary Advance"
                  type="number"
                  value={inputForm.salary_advance}
                  onChange={(e) => setInputForm({ ...inputForm, salary_advance: parseFloat(e.target.value) || 0 })}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Personal Advance"
                  type="number"
                  value={inputForm.personal_advance}
                  onChange={(e) => setInputForm({ ...inputForm, personal_advance: parseFloat(e.target.value) || 0 })}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Professional Tax (PT)"
                  type="number"
                  value={inputForm.professional_tax}
                  onChange={(e) => setInputForm({ ...inputForm, professional_tax: parseFloat(e.target.value) || 0 })}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="TDS"
                  type="number"
                  value={inputForm.tds}
                  onChange={(e) => setInputForm({ ...inputForm, tds: parseFloat(e.target.value) || 0 })}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Variable Allowance"
                  type="number"
                  value={inputForm.variable_allowance}
                  onChange={(e) => setInputForm({ ...inputForm, variable_allowance: parseFloat(e.target.value) || 0 })}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Other Allowance"
                  type="number"
                  value={inputForm.other_allowance}
                  onChange={(e) => setInputForm({ ...inputForm, other_allowance: parseFloat(e.target.value) || 0 })}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Special Incentive"
                  type="number"
                  value={inputForm.special_incentive}
                  onChange={(e) => setInputForm({ ...inputForm, special_incentive: parseFloat(e.target.value) || 0 })}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Other Deductions/Fine"
                  type="number"
                  value={inputForm.manual_adjustments}
                  onChange={(e) => setInputForm({ ...inputForm, manual_adjustments: parseFloat(e.target.value) || 0 })}
                />
              </Grid>
              <Grid item xs={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={inputForm.pf_applicable}
                      onChange={(e) => setInputForm({ ...inputForm, pf_applicable: e.target.checked })}
                    />
                  }
                  label="PF Applicable"
                />
              </Grid>
              <Grid item xs={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={inputForm.esi_applicable}
                      onChange={(e) => setInputForm({ ...inputForm, esi_applicable: e.target.checked })}
                    />
                  }
                  label="ESI Applicable"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <Typography variant="caption" sx={{ mb: 0.5, fontWeight: "600", color: "#64748b" }}>Payroll Generation Status</Typography>
                  <Select
                    value={inputForm.status}
                    onChange={(e) => setInputForm({ ...inputForm, status: e.target.value })}
                  >
                    <MenuItem value="Draft">Active (Include in Payroll)</MenuItem>
                    <MenuItem value="Hold">On Hold (Skip & Keep ₹0)</MenuItem>
                    <MenuItem value="Stopped">Stopped (Do Not Generate)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setInputModalOpen(false)} sx={{ textTransform: "none" }}>Cancel</Button>
            <Button onClick={handleSaveInputs} variant="contained" sx={{ textTransform: "none" }}>Save</Button>
          </DialogActions>
        </Dialog>

        {/* EXPORT COLUMN SELECTION DIALOG */}
        <Dialog open={exportDialogOpen} onClose={() => setExportDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ fontWeight: "bold" }}>Select Columns to Export</DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2}>
              {Object.keys(exportColumnLabels).map((colKey) => (
                <Grid item xs={6} key={colKey}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectedExportColumns[colKey]}
                        disabled={colKey === "employee_name"}
                        onChange={(e) =>
                          setSelectedExportColumns({
                            ...selectedExportColumns,
                            [colKey]: e.target.checked,
                          })
                        }
                      />
                    }
                    label={exportColumnLabels[colKey]}
                  />
                </Grid>
              ))}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setExportDialogOpen(false)} color="secondary">
              Cancel
            </Button>
            <Button onClick={downloadExcel} variant="contained" color="primary">
              Export
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Layout>
  );
}

export default PayrollProcess;
